import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  LinearProgress,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import { QuestionPageProps } from "../../Interfaces/ComponentProps";
import { Question, Answer } from "../../Interfaces/QuizModel";
import { useGetQuestionsByQuizIdQuery } from "../../Apis/questionApi";
import {
  CompleteQuizWithAnswersRequest,
  AnswerSubmissionDTO,
  useCompleteQuizWithAnswersMutation,
} from "../../Apis/progressApi";
import { ArrowBack, ArrowForward, CheckCircle } from "@mui/icons-material";

const QuestionPage: React.FC<QuestionPageProps> = ({
  quiz,
  username,
  onComplete,
  onClose,
  progressId,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [answeredQuestions, setAnsweredQuestions] = useState<number>(0);
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  const [startTime] = useState<number>(Date.now());
  const [submitting, setSubmitting] = useState(false);

  const { data: questionsData, isLoading, isError } = useGetQuestionsByQuizIdQuery(
    quiz.id
  );
  const [completeQuizWithAnswers] = useCompleteQuizWithAnswersMutation();

  useEffect(() => {
    if (
      questionsData?.result &&
      questionsData.result.length > 0 &&
      answers.length === 0
    ) {
      setAnswers(Array(questionsData.result.length).fill(null));
    }
  }, [questionsData, answers.length]);

  useEffect(() => {
    const answered = answers.filter((a) => a !== null).length;
    setAnsweredQuestions(answered);
  }, [answers]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  if (isLoading) {
    return (
      <Container sx={{ my: 5, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="error" sx={{ textAlign: "center" }}>
          Failed to load questions.
        </Alert>
      </Container>
    );
  }

  if (!questionsData?.result || questionsData.result.length === 0) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="info" sx={{ textAlign: "center" }}>
          No questions available for this quiz.
        </Alert>
      </Container>
    );
  }

  const questions = questionsData.result;
  const progress: number = ((currentQuestion + 1) / questions.length) * 100;

  const handleOptionSelect = (answer: Answer) => {
    setSelectedOption(answer.id);
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer.id;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      const nextIndex = currentQuestion + 1;
      setCurrentQuestion(nextIndex);
      setSelectedOption(answers[nextIndex]);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      const prevIndex = currentQuestion - 1;
      setCurrentQuestion(prevIndex);
      setSelectedOption(answers[prevIndex]);
    }
  };

  const handleFinish = async () => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedOption;
    setAnswers(newAnswers);
    await completeQuiz();
  };

  const completeQuiz = async () => {
    try {
      if (!progressId) throw new Error("Quiz progress not started");
      if (!questionsData?.result) throw new Error("Questions not loaded");

      setSubmitting(true);

      const requestData: CompleteQuizWithAnswersRequest = {
        progressId,
        answers: questionsData.result
          .map((q: Question, i: number) => {
            const selectedAnswerId = answers[i];
            if (selectedAnswerId === null || selectedAnswerId === undefined)
              return undefined;

            const submission: AnswerSubmissionDTO = {
              questionId: q.id,
              selectedAnswerId,
            };
            return submission;
          })
          .filter(
            (a: AnswerSubmissionDTO | undefined): a is AnswerSubmissionDTO =>
              a !== undefined
          ),
      };

      if (requestData.answers.length === 0) throw new Error("No valid answers to submit");

      const result = await completeQuizWithAnswers(requestData).unwrap();

      onComplete({
        score: result.score,
        totalQuestions: result.totalQuestions,
        timeTaken: result.timeTaken,
        timeFactor: result.timeFactor,
        finalScore: result.finalScore,
        correctAnswers: result.correctAnswers,
      });
    } catch (error: any) {
      console.error("Failed to complete quiz:", error);
      alert(error?.message || "Failed to submit quiz. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Container maxWidth="md" sx={{ minHeight: "100vh", py: 5 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ color: "#512384" }}>
          Progress
        </Typography>
        <Chip
          label={`Question ${currentQuestion + 1} of ${questions.length}`}
          color="default"
        />
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress variant="determinate" value={progress} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Answered: {answeredQuestions} / {questions.length}
          </Typography>
          <Typography variant="body2" color="error">
            ⏱ {formatTime(timeElapsed)}
          </Typography>
        </Box>
      </Box>

      {/* Question Card */}
      <Card sx={{ mb: 5 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {questions[currentQuestion].name}
          </Typography>

          <Stack spacing={2} mt={2}>
            {questions[currentQuestion].answers.map(
              (answer: Answer, index: number) => (
                <CardActionArea
                  key={answer.id}
                  onClick={() => handleOptionSelect(answer)}
                  sx={{
                    border:
                      selectedOption === answer.id
                        ? "2px solid #512384"
                        : "1px solid #ddd",
                    bgcolor:
                      selectedOption === answer.id
                        ? "rgba(81,35,132,0.1)"
                        : "inherit",
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        mr: 2,
                        borderRadius: "50%",
                        bgcolor:
                          selectedOption === answer.id ? "#512384" : "#eee",
                        color: selectedOption === answer.id ? "#fff" : "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {String.fromCharCode(65 + index)}
                    </Box>
                    <Typography>{answer.name}</Typography>
                  </Box>
                </CardActionArea>
              )
            )}
          </Stack>

          {/* Navigation */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
            >
              <ArrowBack />
            </Button>

            <Typography variant="body2" sx={{ alignSelf: "center" }}>
              Question {currentQuestion + 1} of {questions.length}
            </Typography>

            {currentQuestion === questions.length - 1 ? (
              <Button
                variant="contained"
                color="success"
                onClick={handleFinish}
                disabled={selectedOption === null || submitting}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <CheckCircle />
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={selectedOption === null}
              >
                <ArrowForward />
              </Button>
            )}
          </Box>

          {/* Finish Early */}
          {answeredQuestions === questions.length &&
            currentQuestion < questions.length - 1 && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleFinish}
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {submitting
                    ? "Submitting..."
                    : "All questions answered - Finish Early"}
                </Button>
              </Box>
            )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default QuestionPage;
